import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { paths } from '@/constants/paths'
import { LocalizedCountdown } from '@/molecules/LocalizedCountdown'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'

interface LivestreamCountdownProps {
  bannerCloudinaryPath: string
  description: string
  name: string
  startAt: string
  streamId: string
  projectSlug: string
}

export interface LivestreamRsvp {
  id: string
  rsvpConfirm: boolean
  calendarConfirm: boolean
}

const responsiveAR = { xs: '16:9', sm: '16:9' }

export const LivestreamCountdown: React.FC<LivestreamCountdownProps> = ({
  bannerCloudinaryPath,
  description,
  name,
  startAt,
  streamId,
  projectSlug,
}) => {
  const { isLoggedIn, user } = useUser()
  const { replace, push: routerPush } = useSafeRouter()
  const searchParams = useSearchParams()
  const url = `${paths.getBaseURL()}${paths.livestream.index}/${streamId}`
  const { loginUrl, trackLoginStarted } = useLoginUrl(`${url}?completed=true`)
  const { t } = useTranslate('livestream')
  const track = useSafeTrack()
  const [showCalendar, setShowCalendar] = useState(false)
  const [rsvpComplete, setRsvpComplete] = useState(false)
  const event = {
    title: name,
    description: `${description}\n\n${t('watchForFreeHere', 'Watch for free here: {{url}}', { url })}`,
    location: '',
    start: startAt,
    end: new Date(new Date(startAt).getTime() + 1 * 60 * 60 * 1000).toISOString(),
  }
  const livestreamRsvpList = useMemo(() => {
    return localStorage.livestreamRsvp ? JSON.parse(localStorage.livestreamRsvp) : []
  }, [])

  const onHandleRsvp = useCallback(() => {
    if (!isLoggedIn) {
      !isLoggedIn && routerPush(loginUrl)
      trackLoginStarted('livestream-rsvp')
      return
    }
    if (livestreamRsvpList && !livestreamRsvpList.includes(streamId)) {
      livestreamRsvpList.push({
        id: streamId,
        rsvpConfirm: true,
        calendarConfirm: false,
      })
      localStorage.setItem('livestreamRsvp', JSON.stringify(livestreamRsvpList))
      setRsvpComplete(true)
      setTimeout(() => {
        setRsvpComplete(false)
      }, 3000)
      track('Livestream RSVP Completed', {
        guid: streamId,
        email: user?.email,
        projectSlug,
      })
    }
  }, [
    isLoggedIn,
    livestreamRsvpList,
    streamId,
    routerPush,
    loginUrl,
    trackLoginStarted,
    track,
    user?.email,
    projectSlug,
  ])

  useEffect(() => {
    if (Boolean(searchParams.has('completed')) && isLoggedIn) {
      onHandleRsvp()
      const searchParamsToUpdate = new URLSearchParams(searchParams.toString())
      searchParamsToUpdate.delete('complete')
      replace(`/livestreams/${streamId}`)
    }
  }, [onHandleRsvp, isLoggedIn, streamId, replace, searchParams])

  const hasRsvp =
    livestreamRsvpList &&
    livestreamRsvpList.some((livestream: LivestreamRsvp) => livestream.id === streamId && livestream.rsvpConfirm)

  const onAddToCalendar = () => {
    setShowCalendar(true)
  }

  return (
    <>
      <div className="aspect-h-9 aspect-w-16 relative h-full w-full bg-black lg:aspect-h-unset lg:aspect-w-unset lg:h-[60vh]">
        <ResponsiveBackgroundImage backgroundSize="bg-contain" src={bannerCloudinaryPath} responsiveAR={responsiveAR} />
        <div className="flex flex-col items-center justify-center">
          <LocalizedCountdown
            onHandleRsvp={onHandleRsvp}
            onAddToCalendar={onAddToCalendar}
            startAt={startAt}
            hasRsvp={hasRsvp}
            rsvpComplete={rsvpComplete}
            showCalendar={showCalendar}
            event={event}
            livestreamRsvpList={livestreamRsvpList}
            streamId={streamId}
          />
        </div>
      </div>
    </>
  )
}
